<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { formatNumber, parseFormattedNumber } from "chartjs-graph";
import { Action } from "@/modules/rule_engine/ruleEngineClasses";
import { describeAction } from "@/modules/rule_engine/describe";
import { useRuleEnginedStore } from "@/modules/rule_engine/store";
import RuleSegments from "@/modules/rule_engine/components/RuleSegments.vue";

const { t } = useI18n();
const store = useRuleEnginedStore();

const action = defineModel<Action>({ required: true });

defineProps<{
	index: number;
	expanded: boolean;
}>();

const emit = defineEmits<{ remove: []; toggle: [] }>();

/** Output variables exist only where the model produces them (pricing). */
const isModifyingOutputVariables = computed(
	() => store.availableActionVariables.length > 0
);

const modifierTypes = computed(() =>
	isModifyingOutputVariables.value
		? ["modify_percentage", "modify_absolute", "set_absolute"]
		: ["add_task_to_bex_reservation", "add_label_to_bex_reservation"]
);

const directActionValues = computed(
	() => store.ruleEngineState.values_for_action_type[action.value.type] ?? []
);

const amountInput = ref<string>(
	"amount" in action.value
		? formatNumber(
				action.value.amount,
				action.value.type === "modify_percentage" ? "percentage" : "default"
		  )
		: "0"
);

/**
 * Reads the amount box under `type`'s formatting, falling back to the stored
 * amount when it cannot be parsed. Switching unit keeps the digits on screen
 * and rescales the stored number to match ("10%" ⇄ "10").
 */
const readAmountInput = (type: Action["type"]): number => {
	const sanitized = amountInput.value
		.replace(/[^\d.,-]/g, "")
		.replace(",", ".");
	const parsed = parseFormattedNumber(
		sanitized,
		type === "modify_percentage" ? "percentage" : "default"
	);

	if (!isNaN(parsed)) return parsed;

	return "amount" in action.value ? action.value.amount : 0;
};

const commitAmountInput = () => {
	if (!("amount" in action.value)) return;

	action.value = {
		...action.value,
		amount: readAmountInput(action.value.type),
	};
};

/**
 * Switching between action types changes which id field is meaningful, so the
 * action is rebuilt rather than patched.
 *
 * Rebuilt in exactly ONE assignment: while the parent binds `v-model`,
 * `defineModel` leaves its local value untouched until the new prop arrives
 * (see `useModel` in runtime-core). A second read of `action.value` in this
 * same tick still returns the pre-change action, so writing twice emits the
 * old type back and the change appears to do nothing.
 */
const changeType = (type: Action["type"]) => {
	if (
		type === "modify_percentage" ||
		type === "modify_absolute" ||
		type === "set_absolute"
	) {
		action.value = {
			uuid: action.value.uuid,
			type,
			variable:
				"variable" in action.value
					? action.value.variable
					: store.availableActionVariables[0]?.name ?? "",
			amount: readAmountInput(type),
		};
		return;
	}

	const firstValue = (store.ruleEngineState.values_for_action_type[type] ??
		[])[0];

	action.value =
		type === "add_task_to_bex_reservation"
			? { uuid: action.value.uuid, type, task_id: firstValue?.uuid ?? "" }
			: { uuid: action.value.uuid, type, label_id: firstValue?.uuid ?? "" };
};

watch(
	() => ("amount" in action.value ? action.value.amount : null),
	(amount) => {
		if (amount === null) return;
		amountInput.value = formatNumber(
			amount,
			action.value.type === "modify_percentage" ? "percentage" : "default"
		);
	}
);

/** Only the modify variants carry a variable; narrow before writing back. */
const variable = computed({
	get: () => ("variable" in action.value ? action.value.variable : null),
	set: (value: Action extends { variable: infer V } ? V : never) => {
		if (!("variable" in action.value)) return;
		action.value = { ...action.value, variable: value };
	},
});

const targetId = computed({
	get: () =>
		action.value.type === "add_task_to_bex_reservation"
			? action.value.task_id
			: action.value.type === "add_label_to_bex_reservation"
			? action.value.label_id
			: "",
	set: (value: string) => {
		if (action.value.type === "add_task_to_bex_reservation") {
			action.value = { ...action.value, task_id: value };
		} else if (action.value.type === "add_label_to_bex_reservation") {
			action.value = { ...action.value, label_id: value };
		}
	},
});

const isComplete = computed(() => {
	if ("amount" in action.value) {
		return !!action.value.variable && !isNaN(action.value.amount);
	}
	return !!targetId.value;
});

const summary = computed(() =>
	describeAction(action.value, t, {
		actionValues: store.ruleEngineState.values_for_action_type,
	})
);

defineExpose({ isComplete });
</script>

<template>
	<div class="action-row">
		<div class="d-flex align-center summary-line" @click="emit('toggle')">
			<v-icon size="14" class="mr-2 chevron">
				{{ expanded ? "mdi-chevron-down" : "mdi-chevron-right" }}
			</v-icon>

			<rule-segments
				:connector="index === 0 ? t('rule_engine.then') : t('rule_engine.and')"
				:segments="summary"
			/>

			<v-icon v-if="!isComplete" size="16" color="warning" class="ml-2">
				mdi-alert-circle-outline
			</v-icon>

			<v-spacer />

			<v-btn
				icon
				variant="text"
				density="comfortable"
				color="error"
				v-tooltip="'Remove action'"
				@click.stop="emit('remove')"
			>
				<v-icon size="18">fa: fa-light fa-trash-can</v-icon>
			</v-btn>
		</div>

		<v-expand-transition>
			<div v-if="expanded" class="editor pt-2">
				<div class="field-row" v-if="isModifyingOutputVariables">
					<label class="field-label">Variable</label>
					<v-select
						v-model="variable"
						:items="store.availableActionVariables"
						:item-title="
							(item) => t(`rule_engine.action_variables.${item.name}`)
						"
						:item-value="(item) => item.name"
						variant="filled"
						density="compact"
						hide-details
					/>
				</div>

				<div class="field-row">
					<label class="field-label">Action</label>
					<v-select
						:model-value="action.type"
						@update:model-value="changeType"
						:items="modifierTypes"
						:item-title="
							(item) => t(`rule_engine.action_modifier_types.${item}`)
						"
						:item-value="(item) => item"
						variant="filled"
						density="compact"
						hide-details="auto"
					/>
				</div>

				<div class="field-row" v-if="isModifyingOutputVariables">
					<label class="field-label">Amount</label>
					<v-text-field
						v-model="amountInput"
						@blur="commitAmountInput"
						@keyup.enter="commitAmountInput"
						variant="filled"
						density="compact"
						hide-details="auto"
						hint="Negative values are allowed"
						persistent-hint
					/>
				</div>

				<div class="field-row" v-else>
					<label class="field-label">Value</label>
					<v-select
						v-model="targetId"
						:items="directActionValues"
						item-title="name"
						item-value="uuid"
						variant="filled"
						density="comfortable"
						hide-details="auto"
					/>
				</div>
			</div>
		</v-expand-transition>
	</div>
</template>

<style scoped lang="scss">
.action-row {
	border-left: 5px solid #e67e22;
	padding: 0.25rem 0 0.25rem 0.75rem;

	& + & {
		margin-top: 0.5rem;
	}
}

.summary-line {
	cursor: pointer;
	min-height: 40px;
}

.chevron {
	opacity: 0.6;
}

.field-row {
	display: grid;
	grid-template-columns: 7rem 1fr;
	align-items: start;
	gap: 0.75rem;
	margin-bottom: 0.75rem;
}

.field-label {
	padding-top: 0.75rem;
	font-size: 0.85rem;
	opacity: 0.75;
}
</style>
